import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import renderAccountDetails from "../../Organisms/Modals/Banking/account-details";
import renderBankStatements from "../../Organisms/Modals/Banking/bank-statements";

import { useModalContext } from "../../Contexts/ModalContext";

import { useDeals } from "../../CustomHooks/Queries";

import { isSVDApproved } from "../../Utils/helpers";

const ChevronIcon = () => (
  <svg className="chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="#8B8FA3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function BankingServices({ stakeDebit = {} }) {
  const modalContext = useModalContext();
  const navigate = useNavigate();

  const { data: deals } = useDeals();

  const cashout = deals?.data?.data?.currentDeal?.cashout ?? {};

  const IsSVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const balance = stakeDebit?.data?.data?.available ?? 0;

  const hasDebitCardId = useMemo(() => {
    const { virtual_card_id, card_id } = stakeDebit?.data?.data ?? {};
    return virtual_card_id != null || card_id != null;
  }, [stakeDebit?.data]);

  const handleManageCardsClick = () => navigate("/manage-stake-debit");

  const handleOnCashOutClick = () => navigate("/cash-out");

  const rows = [
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path d="M3 5l5.5-3 5.5 3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 5v6h9V5" stroke="#636879" strokeWidth="1.2" />
          <path d="M6 8v3M8.5 8v3M11 8v3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
          <path d="M3 11h11" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      ),
      title: "Connected Accounts",
      sub: "Manage linked banks",
      onClick: () => navigate("/connected-accounts"),
      show: true,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <circle cx="8.5" cy="8.5" r="6.5" stroke="#636879" strokeWidth="1.2" />
          <path d="M6 8.5l2 2 3.5-3.5" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Recurring Deposits",
      sub: "Manage automatic transfers",
      onClick: () => navigate("/recurring-deposits"),
      show: IsSVDApproved,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path d="M4 8.5h9m0 0l-3-3m3 3l-3 3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Express Paycheck",
      sub: "Up to 2 days early",
      onClick: () => navigate("/direct-deposit"),
      show: IsSVDApproved,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <rect x="3" y="2" width="11" height="13" rx="1.5" stroke="#636879" strokeWidth="1.2" />
          <path d="M6.5 6h4M6.5 9h4M6.5 12h2" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      ),
      title: "Bank Statements",
      sub: "Download monthly statements",
      onClick: () => renderBankStatements(modalContext),
      show: IsSVDApproved,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <rect x="2" y="4" width="13" height="9" rx="1.5" stroke="#636879" strokeWidth="1.2" />
          <circle cx="12" cy="8.5" r="1" fill="#636879" />
        </svg>
      ),
      title: "Manage Cards",
      sub: "Virtual and physical cards",
      onClick: handleManageCardsClick,
      show: IsSVDApproved && balance > 0 && hasDebitCardId,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path d="M3 5l5.5-3 5.5 3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 5v6h9V5" stroke="#636879" strokeWidth="1.2" />
          <path d="M6 8v3M8.5 8v3M11 8v3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
          <path d="M3 11h11" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      ),
      title: "Account & Routing Number",
      sub: "View account details",
      onClick: () => renderAccountDetails(modalContext, stakeDebit?.data),
      show: IsSVDApproved,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <circle cx="8.5" cy="8.5" r="6.5" stroke="#636879" strokeWidth="1.2" />
          <path d="M8.5 5.5v3.5" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
          <circle cx="8.5" cy="11.5" r="0.7" fill="#636879" />
        </svg>
      ),
      title: "Banking Limits",
      sub: "View transfer and spending limits",
      onClick: () => navigate("/banking-limits"),
      show: IsSVDApproved,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path d="M8.5 14V3m0 0l-3 3m3-3l3 3" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Cash Out",
      sub: "Withdraw to external account",
      onClick: handleOnCashOutClick,
      show: !!cashout.is_external_bank_allowed,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <path
            d="M13.5 3.5c.6 1 .9 2.1.9 3.3 0 3.4-2.9 7.7-5.3 10.7H4.4L2.2 3.9l3.7-.4 1.2 9.4c1.1-1.8 2.5-4.6 2.5-6.6 0-1.1-.2-1.8-.5-2.4l4.4-.4z"
            stroke="#636879"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: "Venmo Transfer",
      sub: "Send instantly to your Venmo",
      onClick: () => navigate("/venmo-transfer"),
      show: !!cashout.is_venmo_allowed,
    },
    {
      icon: (
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
          <rect x="2" y="3.5" width="13" height="10" rx="1.2" stroke="#636879" strokeWidth="1.2" strokeLinejoin="round" />
          <path d="M2 6.5h13" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
          <path d="M4.5 10h3.5M4.5 11.5h2.5" stroke="#636879" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      ),
      title: "Mail a Check",
      sub: "Send a paper check to your address",
      onClick: () => navigate("/mail-a-check"),
      show: !!cashout.is_check_allowed,
    },
  ];

  const visibleRows = rows.filter((r) => r.show);

  return (
    <div className="banking-services">
      <div className="bs-header">
        <h3>Banking Services</h3>
      </div>
      {visibleRows.map((row) => (
        <div key={row.title} className="bs-row" onClick={row.onClick}>
          <div className="bs-icon">{row.icon}</div>
          <div className="bs-info">
            <span className="title">{row.title}</span>
            <span className="sub">{row.sub}</span>
          </div>
          <ChevronIcon />
        </div>
      ))}
    </div>
  );
}
